import React from "react";
import { useT } from "../../contexts/TranslationContext";
import { displayText } from "../../utils/translationDisplay";

/**
 * Component displaying list of task time entries with hours input and optional descriptions
 * Shows estimated and tracked hours when available
 * @param {boolean} [hideAddTask] - When true, hide the "Add task" button (e.g. for inline edit)
 * @param {boolean} [allowRemove=true] - When false, hide the remove button on each entry
 */
function TaskTimeEntryList({ entries, onUpdateHours, onUpdateDescription, onRemove, onAddClick, hideAddTask = false, allowRemove = true, hideLabel = false, hideBillingModel = false, hideInputHourSuffix = false }) {
  const { t } = useT();

  const formatHours = (hours) => {
    if (hours === undefined || hours === null) return null;
    return `${Number(hours).toFixed(1)}${t("timeReports.hourSuffix")}`;
  };
  const totalHours = entries.reduce((sum, entry) => {
    const hours = parseFloat(entry.hours) || 0;
    return sum + hours;
  }, 0);

  return (
    <div className="space-y-3">
      {!hideLabel && <label className="block font-medium">{t("report.taskEntries")}</label>}
      
      {entries.length === 0 ? (
        <div className="text-gray-500 text-sm py-2">
          {t("report.noTasksAddedYet")}
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry, idx) => {
            const currentHours = parseFloat(String(entry.hours).replace(",", ".")) || 0;
            const decrementHours = () => {
              const newValue = Math.max(0, currentHours - 0.5);
              onUpdateHours(idx, newValue === 0 ? "" : String(newValue));
            };
            const incrementHours = () => {
              const newValue = currentHours + 0.5;
              onUpdateHours(idx, String(newValue));
            };
            
            // Calculate progress if we have both estimated and tracked hours
            const hasEstimate = entry.estimatedHours !== undefined && entry.estimatedHours !== null && entry.estimatedHours > 0;
            const trackedHours = entry.trackedHours || 0;
            const progressPercent = hasEstimate ? Math.min(100, (trackedHours / entry.estimatedHours) * 100) : 0;
            const isOverBudget = hasEstimate && trackedHours > entry.estimatedHours;
            
            return (
              <div
                key={entry.localDraftId || `${entry.taskId}-${entry.subtaskId || "task"}-${idx}`}
                className="p-3 bg-gray-50 rounded-lg space-y-2"
              >
                <div className="flex items-center gap-2">
                  {/* Remove button */}
                  {allowRemove && onRemove && (
                    <button
                      type="button"
                      onClick={() => onRemove(idx)}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                  
                  {/* Task info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900 truncate text-sm">{displayText(entry, "title") || entry.title}</div>
                      {!hideBillingModel && entry.billingModel && (
                        <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                          {entry.billingModel === 'hourly' ? t("report.billingHourly") : entry.billingModel === 'fixed_price' ? t("report.billingFixedPrice") : ''}
                        </span>
                      )}
                    </div>
                    {entry.type === "subtask" && (displayText(entry, "taskTitle") || entry.taskTitle) && (
                      <div className="text-xs text-gray-500 truncate">{displayText(entry, "taskTitle") || entry.taskTitle}</div>
                    )}
                    {entry.isPending && (
                      <div className="text-xs text-amber-600">{t("reportFlags.pendingApproval")}</div>
                    )}
                    {/* Hours progress indicator */}
                    {(hasEstimate || trackedHours > 0) && (
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden max-w-[100px]">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              isOverBudget ? 'bg-red-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                        <span className={`text-xs ${isOverBudget ? 'text-red-600' : 'text-gray-500'}`}>
                          {formatHours(trackedHours)}
                          {hasEstimate && ` / ${formatHours(entry.estimatedHours)}`}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Hours input with +/- buttons */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={decrementHours}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={entry.hours}
                      onChange={(e) => onUpdateHours(idx, e.target.value)}
                      placeholder="0"
                      className="w-12 px-1 py-1.5 text-center text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={incrementHours}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    {!hideInputHourSuffix && (
                      <span className="text-gray-500 text-sm ml-0.5">{t("timeReports.hourSuffix")}</span>
                    )}
                  </div>
                </div>
                
                {/* Description field - shown if showDescriptionInTimeReport is true */}
                {entry.showDescriptionInTimeReport && onUpdateDescription && (
                  <div className={allowRemove ? "pl-9" : ""}>
                    <textarea
                      type="text"
                      value={entry.description || ""}
                      onChange={(e) => onUpdateDescription(idx, e.target.value)}
                      placeholder={entry.requireDescriptionInTimeReport ? t("report.descriptionRequiredPlaceholder") : t("report.descriptionOptionalPlaceholder")}
                      rows={2}
                      required={!!entry.requireDescriptionInTimeReport}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                        entry.requireDescriptionInTimeReport && !(entry.description || "").trim()
                          ? "border-amber-500 focus:ring-amber-500"
                          : ""
                      }`}
                      aria-invalid={entry.requireDescriptionInTimeReport && !(entry.description || "").trim()}
                    />
                    {entry.requireDescriptionInTimeReport && (
                      <div className="text-xs text-amber-600 mt-1">{t("report.requiredWhenLoggingTime")}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Add task button */}
      {!hideAddTask && onAddClick && (
        <button
          type="button"
          onClick={onAddClick}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t("report.addTask")}
        </button>
      )}
      
      {/* Total */}
      {entries.length > 0 && (
        <div className="flex justify-between items-center pt-2 border-t">
          <span className="font-medium text-gray-700">{t("report.total")}</span>
          <span className="font-semibold text-lg">{totalHours.toFixed(1)}{t("timeReports.hourSuffix")}</span>
        </div>
      )}
    </div>
  );
}

export default TaskTimeEntryList;
